import { useState, useMemo } from "react";
import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import { getNodeTypeLabel, NODE_TYPE_CATEGORIES } from "./node-type-selector";
import { RESTRICTED_NODE_TYPES } from "./types";
import { Icon } from "@elaniintc/goes-design-system";
import { NodeTypes } from "@/workflow_api/types/workflow";

interface NodeTypeSelectorProps {
  value: string;
  onChange: (nodeType: string) => void;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}

const NODE_TYPE_ICONS: Record<string, { type: string; name: string }> = {
  [NodeTypes.FORM]: { type: "fas", name: "bars" },
  [NodeTypes.CORRECTION]: { type: "fas", name: "file-edit" },
  [NodeTypes.IF]: { type: "fas", name: "toggle-on" },
  [NodeTypes.SWITCH]: { type: "fas", name: "arrows-cross" },
  [NodeTypes.MULTITHREAD]: { type: "fas", name: "code-branch" },
  [NodeTypes.WAIT]: { type: "fas", name: "hourglass-half" },
  [NodeTypes.MERGE]: { type: "fas", name: "code-merge" },
  [NodeTypes.ASSIGN_GROUP]: { type: "fas", name: "users" },
  [NodeTypes.DATA_CENTER]: { type: "fas", name: "table" },
  [NodeTypes.EXPEDIENTE]: { type: "fas", name: "folder-open" },
  [NodeTypes.NOTIFICATION]: { type: "fas", name: "bells" },
  [NodeTypes.CALCULATED]: { type: "fas", name: "function" },
  [NodeTypes.PAYMENT]: { type: "fas", name: "dollar-sign" },
  [NodeTypes.N8N]: { type: "fas", name: "hexagon-nodes" },
  [NodeTypes.INTEROPERABILITY]: { type: "fas", name: "diagram-nested" },
  [NodeTypes.DOC_GENERATOR]: { type: "fas", name: "file-lines" },
  [NodeTypes.SIGN_DOCUMENT]: { type: "fas", name: "signature" },
  [NodeTypes.RESOLUTION]: { type: "fas", name: "files" },
};

const CATEGORY_COLORS: Record<
  string,
  "action" | "success" | "warning" | "info" | "neutral" | "danger"
> = {
  OPERATORS: "neutral",
  ACTIONS: "info",
  DATA_TRANSFORM: "success",
  INTEGRATIONS: "warning",
  DOCUMENTS: "action",
};

export const NodeTypeSelector = ({
  value,
  onChange,
  label,
  required,
  error,
  disabled,
}: NodeTypeSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const groups: DropdownListGroup[] = useMemo(() => {
    console.log("RESTRICTED_NODE_TYPES:", RESTRICTED_NODE_TYPES);

    return Object.entries(NODE_TYPE_CATEGORIES)
      .map(([key, category]) => {
        const filteredItems: IDropdownListItem[] = category.types
          .filter((type) => {
            console.log("Checking type:", type.value, "Label:", type.label);
            const isRestricted = RESTRICTED_NODE_TYPES.includes(
              type.value as (typeof RESTRICTED_NODE_TYPES)[number],
            );
            console.log("Is restricted:", isRestricted);
            return !isRestricted;
          })
          .filter((type) =>
            type.label.toLowerCase().includes(searchTerm.toLowerCase()),
          )
          .map((type) => ({
            value: type.value,
            label: type.label,
            icon: NODE_TYPE_ICONS[type.value] || { type: "fas", name: "cube" },
          }));

        return {
          groupId: key,
          groupLabels: [category.title],
          items: filteredItems,
          icon: {
            type: "fas",
            name:
              key === "OPERATORS"
                ? "toggle-on"
                : key === "ACTIONS"
                  ? "grid-2-plus"
                  : key === "DATA_TRANSFORM"
                    ? "right-left-large"
                    : key === "INTEGRATIONS"
                      ? "object-intersect"
                      : "file-lines",
            variant: CATEGORY_COLORS[key] || "neutral",
          },
        };
      })
      .filter((group) => group.items.length > 0);
  }, [searchTerm]);

  const selectedValue: IDropdownListItem | undefined = useMemo(() => {
    if (!value) return undefined;
    const label = getNodeTypeLabel(value);
    return {
      value,
      label,
      icon: NODE_TYPE_ICONS[value] || { type: "fas", name: "cube" },
    };
  }, [value]);

  const selectedGroup: DropdownListGroup | undefined = useMemo(() => {
    if (!value) return undefined;
    return groups.find((g) => g.items.some((i) => i.value === value));
  }, [value, groups]);

  return (
    <div className="flex flex-col gap-1 w-full">
      {label && (
        <label className="font-semibold text-body-loud text-sm leading-2xl">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <DropdownList
        options={groups}
        selectedValue={selectedValue}
        selectedGroup={selectedGroup}
        placeholder="Seleccionar tipo de nodo"
        widthClassName="w-full"
        disabled={disabled}
        search={{
          placeholder: "Buscar tipo de nodo...",
          query: searchTerm,
          onSearch: setSearchTerm,
        }}
        toggleSelection={(item) => {
          onChange(item.value);
        }}
      />

      {error && (
        <div className="mt-1 text-sm text-danger flex items-center gap-1">
          <Icon type="far" name="circle-xmark" />
          {error}
        </div>
      )}
    </div>
  );
};
